import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { logoutRequest } from '../../services/auth';
import { callApiThunk } from '../../utils/callApiThunk';

const AUTH_BASE_URL = `${process.env.REACT_APP_API_BASE_URL}/web/auth`;
const LOGIN_URL = `${AUTH_BASE_URL}/login`;
const GET_USER_PROFILE_URL = `${process.env.REACT_APP_API_BASE_URL}/web/users/get-user-profile`;

const initialState = {
    isAuthenticated: true,
    initialAuthLoading: true,
    authLoading: true,
    data: {},
    userRole: 'admin',
    accessToken: '',
    refreshToken: ''
};

export const login = createAsyncThunk('auth/login', async (data, thunkApi) => {
    const { rejectWithValue } = thunkApi;
    const { email, password } = data;

    const result = await callApiThunk({
        requestFunction: (axios) => axios.post(`${LOGIN_URL}`, { email, password }),
        successCode: 200,
        callRefreshTokenOnAuthError: false,
        logOutOnAuthError: false,
        showToastOnError: true,
        showToastOnSuccess: false,
        thunkApi,
        useAuth: false
    });

    if (result.error === false) {
        return result;
    }
    return rejectWithValue(result);
});

export const logout = createAsyncThunk('auth/logout', async (data, { rejectWithValue, getState }) => {
    const { accessToken, refreshToken } = getState().tokens;
    const responseData = await logoutRequest({
        accessToken,
        refreshToken
    });
    if (responseData.error) {
        rejectWithValue(responseData);
    }
    return responseData;
});

export const getProfile = createAsyncThunk('auth/getUserProfile', async (data, thunkApi) => {
    const { rejectWithValue } = thunkApi;

    const result = await callApiThunk({
        requestFunction: (axios) => axios.get(`${GET_USER_PROFILE_URL}`),
        successCode: 200,
        callRefreshTokenOnAuthError: true,
        thunkApi,
        showToastOnSuccess: false,
        showToastOnError: false
    });

    if (result.error === false) {
        return result;
    }
    return rejectWithValue(result);
});

const authSlice = createSlice({
    name: 'auth',
    initialState,
    reducers: {
        setTokens: (state, action) => {
            const { accessToken, refreshToken } = action.payload;
            state.accessToken = accessToken;
            state.refreshToken = refreshToken;
        }
    },
    extraReducers: {
        [login.pending]: (state) => {
            state.authLoading = true;
        },
        [login.fulfilled]: (state, action) => {
            state.authLoading = false;
            state.isAuthenticated = true;
            state.accessToken = action.payload.tokens.access.token;
            state.refreshToken = action.payload.tokens.refresh.token;
            state.data = action.payload.user;
        },
        [login.rejected]: (state) => {
            state.authLoading = false;
            state.isAuthenticated = false;
            state.accessToken = '';
            state.refreshToken = '';
        },
        [logout.pending]: (state) => {
            state.authLoading = true;
        },
        [logout.fulfilled]: (state) => {
            state.authLoading = false;
            state.isAuthenticated = false;
            state.accessToken = '';
            state.refreshToken = '';
            state.data = {};
        },
        [logout.rejected]: (state) => {
            state.authLoading = false;
            state.isAuthenticated = false;
            state.accessToken = '';
            state.refreshToken = '';
            state.data = {};
        },
        [getProfile.pending]: (state) => {
            state.authLoading = true;
        },
        [getProfile.fulfilled]: (state, action) => {
            const user = action?.payload?.user ?? {};
            state.authLoading = false;
            state.initialAuthLoading = false;
            state.isAuthenticated = true;
            state.data = user;
        },
        [getProfile.rejected]: (state) => {
            state.authLoading = false;
            state.initialAuthLoading = false;
            state.isAuthenticated = false;
            state.data = {};
        }
    }
});

export const { setTokens } = authSlice.actions;

// selectors
export const selectAuthLoading = (state) => state.auth.authLoading;
export const selectInitialAuthLoading = (state) => state.auth.initialAuthLoading;
export const selectIsAuthenticated = (state) => state.auth.isAuthenticated;

export const selectUserId = (state) => state.auth.data?.user_id;
export const selectUserName = (state) => state.auth.data?.name;
export const selectUserEmail = (state) => state.auth.data?.email;

export const selectUserLevel = (state) => state.auth.data?.studentLevel;

export const selectUserInstituteName = (state) => state.auth.data?.institute?.name ?? '';
export const selectUserInstituteAddress = (state) => state.auth.data?.institute?.address ?? '';
export const selectUserInstituteImageUrl = (state) => state.auth.data?.institute?.logo ?? '';

export const selectUserYearBookGalleryLimit = (state) => state.auth.data?.yearBook?.galleryLimit ?? 0;
export const selectUserYearBookIncludeGallery = (state) => state.auth.data?.yearBook?.includeGallery ?? false;

export const selectUserYearBookCollageLimit = (state) => state.auth.data?.yearBook?.collageLimit ?? 0;
export const selectUserYearBookIncludeCollage = (state) => state.auth.data?.yearBook?.collagePhoto ?? false;

export const selectUserYearBookTestimonialLimit = (state) => state.auth.data?.yearBook?.characterLimit ?? 0;

export const selectUserYearBookName = (state) => state.auth.data?.yearBook?.name ?? '';
export const selectUserYearBookContactEmail = (state) => state.auth.data?.yearBook?.contactEmail ?? '';
export const selectUserYearBookContactName = (state) => state.auth.data?.yearBook?.contactPerson ?? '';
export const selectUserYearBookContactPhone = (state) => state.auth.data?.yearBook?.contactNumber ?? '';

export const selectUserYearIsFreezeTestimonials = (state) => state.auth.data?.yearBook?.freezeTestimonials ?? true;
export const selectUserYearIsSequenceTestimonials = (state) => state.auth.data?.yearBook?.sequenceTestimonials;
export const selectUserYearBookLastDate = (state) => state.auth.data?.yearBook?.lastDate;

export const selectUserProfileImage = (state) =>
    state?.auth?.data?.image ? `${process.env.REACT_APP_AWS_BASE_PATH}/${state.auth.data?.image}` : '';

export const selectRefreshToken = (state) => state.auth.refreshToken;
export const selectAccessToken = (state) => state.auth.accessToken;
export const selectAccessTokenNew = (state) => state.tokens.accessToken;
export const selectIsRehydrated = (state) => state.auth._persist.rehydrated;

export default authSlice.reducer;
